import { useEffect, useMemo, useState } from 'react';
import { Boxes, Plus } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PaginationState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@ui/button.tsx';
import { Card, CardContent } from '@ui/card.tsx';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import type { DataTableFilterField } from '@r/types/index.ts';
import {
  apiShopsShopIdproductsGetCollection,
  getApiShopsShopIdproductsGetCollectionQueryKey,
  updateProductStatus
} from '@r/api/generated/client.ts';
import type {
  ApiShopsShopIdproductsGetCollectionParams,
  ProductProductReadModel
} from '@r/api/generated/client.schemas.ts';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { isAdminOrHigher, type Role } from '@r/utils/roles.ts';
import { isOutOfStock } from '@r/utils/product-stock.ts';
import { getAdminTablePersistenceKey } from '@r/utils/data-table-persistence.ts';
import { useOrvalErrorToast, useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@ui/dialog.tsx';
import { CreateCustomShopProductForm } from './create-custom-shop-product-form.tsx';
import { useCustomShopProductColumns } from './custom-shop-product-columns.tsx';
import { UpdateCustomShopProductForm } from './update-custom-shop-product-form.tsx';

interface CustomShopCatalogProps {
  shopId: number;
}

const INITIAL_COLUMN_VISIBILITY = {
  search: false
};

/** Affiche le catalogue paginé d'une boutique et pilote les modales de création/édition. */
export function CustomShopCatalog({ shopId }: CustomShopCatalogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const user = useUser();
  const activeWorkspace = getActiveWorkspaceUniversal(user.data?.visibleWorkspaces ?? []);
  const canManageProducts = isAdminOrHigher(activeWorkspace?.maxRole as Role);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [productToEdit, setProductToEdit] = useState<ProductProductReadModel | null>(null);
  const [products, setProducts] = useState<ProductProductReadModel[]>([]);
  const [totalPages, setTotalPages] = useState(1);

  /** Demande confirmation avant d'archiver ou de réactiver un produit. */
  const handleProductStatusChange = (
    product: ProductProductReadModel,
    status: { isActive: boolean; isArchived: boolean },
    confirmKey: string,
    successKey: string,
    errorKey: string
  ) => {
    toast(t(confirmKey, { productName: product.name }), {
      action: {
        label: t('label.confirm'),
        onClick: () => {
          void (async () => {
            try {
              await updateProductStatus(String(shopId), String(product.id), status);
              await queryClient.invalidateQueries({
                queryKey: getApiShopsShopIdproductsGetCollectionQueryKey(String(shopId))
              });
              toast.success(t(successKey));
            } catch (error) {
              showOrvalErrorToast(error, t(errorKey));
            }
          })();
        }
      },
      cancel: {
        label: t('label.cancel'),
        onClick: () => {}
      }
    });
  };

  /** Archive un produit (isActive=false, isArchived=true). */
  const handleArchiveProduct = (product: ProductProductReadModel) => {
    handleProductStatusChange(
      product,
      { isActive: false, isArchived: true },
      'admin.shops.products.archive.confirm',
      'admin.shops.products.archive.success',
      'admin.shops.products.archive.error'
    );
  };

  /** Remet un produit archivé en ligne. */
  const handleReactivateProduct = (product: ProductProductReadModel) => {
    handleProductStatusChange(
      product,
      { isActive: true, isArchived: false },
      'admin.shops.products.reactivate.confirm',
      'admin.shops.products.reactivate.success',
      'admin.shops.products.reactivate.error'
    );
  };

  const columns = useCustomShopProductColumns({
    onEdit: setProductToEdit,
    onArchive: handleArchiveProduct,
    onReactivate: handleReactivateProduct,
    canManageProducts
  });

  const filterFields = useMemo<DataTableFilterField<ProductProductReadModel>[]>(
    () => [
      {
        label: t('admin.shops.products.filter.search'),
        value: 'search',
        placeholder: t('admin.shops.products.filter.search.placeholder')
      },
      {
        label: t('admin.shops.products.filter.name'),
        value: 'name',
        placeholder: t('admin.shops.products.filter.name.placeholder')
      }
    ],
    [t]
  );
  const persistenceKey = useMemo(
    () => getAdminTablePersistenceKey(`shop-${shopId}-products`, user.data?.id),
    [shopId, user.data?.id]
  );

  const { table } = useDataTable({
    data: products,
    columns,
    pageCount: totalPages,
    filterFields,
    enableAdvancedFilter: true,
    initialColumnVisibility: INITIAL_COLUMN_VISIBILITY,
    persistence: { key: persistenceKey }
  });

  const { pagination, sorting, columnFilters } = table.getState();
  const { pageIndex, pageSize } = pagination as PaginationState;
  /** Extrait la valeur textuelle d'un filtre de colonne TanStack Table. */
  const getTextFilter = (id: string) =>
    String(columnFilters.find((filter) => filter.id === id)?.value ?? '').trim();
  const activeSort = sorting[0];

  // TanStack Table indexe les pages à partir de 0 alors que l'API les indexe à partir de 1.
  const params: ApiShopsShopIdproductsGetCollectionParams = {
    page: pageIndex + 1,
    perPage: pageSize,
    ...(getTextFilter('search') && { search: getTextFilter('search') }),
    ...(getTextFilter('name') && { name: getTextFilter('name') }),
    ...(activeSort?.id === 'createdAt' && {
      'order[createdAt]': activeSort.desc ? 'desc' : 'asc'
    }),
    ...(activeSort?.id === 'name' && {
      'order[name]': activeSort.desc ? 'desc' : 'asc'
    }),
    ...(activeSort?.id === 'price' && {
      'order[price]': activeSort.desc ? 'desc' : 'asc'
    })
  };

  const productsQuery = useQuery({
    queryKey: getApiShopsShopIdproductsGetCollectionQueryKey(String(shopId), params),
    queryFn: () => apiShopsShopIdproductsGetCollection(String(shopId), params),
    placeholderData: (previousData) => previousData
  });
  useOrvalQueryErrorToast(productsQuery, t('admin.shops.catalog.error.load'));

  useEffect(() => {
    if (!productsQuery.data) return;

    setProducts(productsQuery.data.data ?? []);
    setTotalPages(productsQuery.data.pagination?.last ?? 1);
  }, [productsQuery.data]);

  return (
    <>
      <Card className="w-full">
        <CardContent className="p-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div className="space-y-2">
              <h2 className="flex items-center gap-3 text-2xl font-bold">
                <Boxes className="size-6" />
                {t('admin.shops.catalog.title')}
              </h2>
              <p className="text-muted-foreground">{t('admin.shops.catalog.description')}</p>
            </div>

            {canManageProducts && (
              <Button type="button" rounded="full" onClick={() => setIsCreateDialogOpen(true)}>
                {t('admin.shops.products.create.button')}
                <Plus className="size-4" />
              </Button>
            )}
          </div>

          <div className="mt-6">
            <DataTableAdvancedToolbar table={table} filterFields={filterFields} />

            {productsQuery.isLoading && (
              <DataTableSkeleton
                columnCount={18}
                rowCount={5}
                cellWidths={['4rem', '5rem', '14rem', '16rem', '10rem', '8rem']}
              />
            )}

            {productsQuery.isSuccess && (
              <DataTable
                table={table}
                totalItems={productsQuery.data.pagination?.totalItems}
                getRowClassName={(product) =>
                  product.isArchived || isOutOfStock(product.stock)
                    ? 'bg-muted/40 opacity-60'
                    : undefined
                }
                className="overflow-x-auto"
              />
            )}

            {productsQuery.isError && (
              <div className="flex flex-col items-center gap-4 rounded-3xl border py-10 text-center">
                <p className="text-muted-foreground">{t('admin.shops.catalog.error.load')}</p>
                <Button
                  type="button"
                  variant="outline"
                  rounded="full"
                  onClick={() => void productsQuery.refetch()}
                >
                  {t('admin.shops.catalog.error.retry')}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {canManageProducts && (
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-3xl overflow-hidden p-0">
            <div className="scrollable max-h-[90vh] overflow-y-auto p-8 md:p-14">
              <DialogHeader>
                <DialogTitle className="text-2xl">
                  {t('admin.shops.products.create.dialog.title')}
                </DialogTitle>
                <DialogDescription>
                  {t('admin.shops.products.create.dialog.description')}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-6">
                <CreateCustomShopProductForm
                  shopId={shopId}
                  onCancel={() => setIsCreateDialogOpen(false)}
                  onSuccess={() => setIsCreateDialogOpen(false)}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Dialog
        open={productToEdit !== null}
        onOpenChange={(open) => {
          if (!open) setProductToEdit(null);
        }}
      >
        <DialogContent className="max-w-3xl overflow-hidden p-0">
          <div className="scrollable max-h-[90vh] overflow-y-auto p-8 md:p-14">
            <DialogHeader>
              <DialogTitle className="text-2xl">
                {t('admin.shops.products.update.dialog.title')}
              </DialogTitle>
              <DialogDescription>
                {t('admin.shops.products.update.dialog.description')}
              </DialogDescription>
            </DialogHeader>
            {productToEdit && (
              <div className="mt-6">
                <UpdateCustomShopProductForm
                  key={productToEdit.id}
                  shopId={shopId}
                  product={productToEdit}
                  onCancel={() => setProductToEdit(null)}
                  onSuccess={() => setProductToEdit(null)}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
